import React from 'react';
import { Formik } from 'formik';

import InstructionsMarkdownEditorFormik from '~/components/Admin/AiExperts/InstructionsMarkdownEditorFormik';
import CardDialog from '~/components/CardDialog';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import Text from '~/components/core/TextComponents/Text';

import copies from '../copies';
import type { GeneralInstructionsFormValues } from '../types';

interface GeneralInstructionsDialogProps {
  generalInstructions: string;
  onClose: () => void;
  /** Persists the value and resolves to the refreshed scope, or `undefined` if the refresh failed. */
  onSave: (generalInstructions: string) => Promise<unknown>;
}

/**
 * Holds a local draft and commits it on Save, so Cancel genuinely discards.
 *
 * Formik owns the draft rather than the parent, which is what makes that true: the section
 * behind the dialog keeps rendering the saved value until the save comes back.
 */
const GeneralInstructionsDialog: React.FC<GeneralInstructionsDialogProps> = ({
  generalInstructions,
  onClose,
  onSave,
}) => {
  const initialValues: GeneralInstructionsFormValues = { general_instructions: generalInstructions };

  const handleSubmit = async (values: GeneralInstructionsFormValues) => {
    // The parent reports its own errors and resolves to undefined rather than throwing, so
    // only close once the save and its refresh actually returned — otherwise a failed save
    // would read as a successful one behind a dialog that had already gone.
    const saved = await onSave(values.general_instructions);
    if (saved !== undefined) {
      onClose();
    }
  };

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
      {({ isSubmitting, handleSubmit: submitForm }) => (
        <CardDialog
          isDialog
          open
          maxWidth="md"
          fullWidth
          title={copies.generalInstructionsDialog.title}
          onClose={onClose}
          preventClose={isSubmitting}
          footerActions={
            <DialogFooterActions
              onClickPrimary={() => submitForm()}
              onClickSecondary={onClose}
              disabled={isSubmitting}
            />
          }
        >
          <div className="flex flex-col gap-8">
            <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
              {copies.generalInstructionsDialog.label}
            </Text>
            <InstructionsMarkdownEditorFormik id="general_instructions" />
            <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
              {copies.editor.generalInstructionsHelper}
            </Text>
          </div>
        </CardDialog>
      )}
    </Formik>
  );
};

export default GeneralInstructionsDialog;
